"use client";

import { cn } from "@/lib/utils";
import { IconMenu2, IconX } from "@/components/icons";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { localizedText } from "@/i18n/text";

const MOBILE_NAV_MENU_ID = "mobile-nav-menu";

export type NavbarItem = {
  name: string;
  link: string;
  dropdown?: {
    name: string;
    link: string;
  }[];
};

interface NavbarProps {
  children: React.ReactNode;
  className?: string;
}

interface NavBodyProps {
  children: React.ReactNode;
  className?: string;
  visible?: boolean;
}

interface NavItemsProps {
  items: NavbarItem[];
  className?: string;
  onItemClick?: () => void;
}

interface MobileNavProps {
  children: React.ReactNode;
  className?: string;
  visible?: boolean;
}

interface MobileNavHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface MobileNavMenuProps {
  children: React.ReactNode;
  className?: string;
  isOpen: boolean;
  onClose: () => void;
}

export const Navbar = ({ children, className }: NavbarProps) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 100);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={cn("fixed inset-x-0 top-20 z-1000 w-full", className)}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(
              child as React.ReactElement<{ visible?: boolean }>,
              { visible },
            )
          : child,
      )}
    </header>
  );
};

export const NavBody = ({ children, className, visible }: NavBodyProps) => {
  return (
    <div
      style={{
        minWidth: "min(960px, calc(100vw - 2rem))",
        backdropFilter: visible ? "blur(10px)" : "none",
        boxShadow: visible
          ? "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset"
          : "none",
      }}
      className={cn(
        "relative z-[60] mx-auto hidden flex-row items-center justify-between self-start rounded-full bg-transparent px-4 py-2 transition-all duration-300 xl:flex dark:bg-transparent",
        visible
          ? "w-[88%] max-w-[1120px] translate-y-5 bg-white/80 dark:bg-neutral-950/80"
          : "w-full max-w-7xl translate-y-0",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const NavItems = ({ items, className, onItemClick }: NavItemsProps) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelScheduledClose = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
  };

  const openDropdown = (idx: number) => {
    cancelScheduledClose();
    setHovered(idx);
  };

  // Stäng med en kort fördröjning i stället för direkt. En diagonal rörelse
  // ner mot panelen skär ofta genom ett grannitem, och utan marginal hinner
  // menyn rivas innan pekaren är framme.
  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimeout.current = setTimeout(() => setHovered(null), 150);
  };

  useEffect(() => cancelScheduledClose, []);

  const { locale } = useI18n();

  return (
    <nav
      aria-label={localizedText(locale, "Huvudmeny", "Main menu")}
      onMouseLeave={scheduleClose}
      className={cn(
        "absolute left-1/2 top-1/2 z-10 hidden -translate-x-1/2 -translate-y-1/2 flex-row items-center justify-center gap-1 text-sm font-medium text-zinc-600 transition duration-200 hover:text-zinc-800 xl:flex",
        className,
      )}
    >
      {items.map((item, idx) => (
        <div
          key={`link-${idx}`}
          className="relative"
          onMouseEnter={() => openDropdown(idx)}
          onMouseLeave={scheduleClose}
          // Tangentbord: fokus in i länken/undermenyn öppnar den, fokus ut
          // stänger, Escape stänger direkt. Samma state som hover använder.
          onFocus={() => openDropdown(idx)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
              setHovered(null);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setHovered(null);
            }
          }}
        >
          <Link
            href={item.link}
            onClick={onItemClick}
            aria-haspopup={item.dropdown && item.dropdown.length > 0 ? "menu" : undefined}
            aria-expanded={
              item.dropdown && item.dropdown.length > 0 ? hovered === idx : undefined
            }
            className="relative block rounded-full px-4 py-2 text-neutral-600 transition-colors hover:text-neutral-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand dark:text-neutral-300 dark:hover:text-white"
          >
            {hovered === idx && (
              <div
                className="absolute inset-0 h-full w-full rounded-full bg-gray-100 shadow-[0_8px_20px_rgba(15,23,42,0.08)] dark:bg-neutral-800"
              />
            )}
            <span className="relative z-20 whitespace-nowrap">{item.name}</span>
          </Link>

          {item.dropdown && item.dropdown.length > 0 && hovered === idx && (
            // pt-3 i stället för en offset i `top`: mellanrummet mellan
            // triggern och kortet måste tillhöra panelen, annars är det dött
            // utrymme där pekaren lämnar menyn på vägen ner.
            <div className="absolute left-1/2 top-full z-50 w-60 -translate-x-1/2 pt-3 animate-in fade-in-0 slide-in-from-top-2 duration-150">
              <div className="overflow-hidden rounded-3xl border border-neutral-200/80 bg-white/95 p-2 shadow-[0_24px_60px_rgba(15,23,42,0.12)] backdrop-blur-xl">
                {item.dropdown.map((subItem) => (
                  <Link
                    key={subItem.link}
                    href={subItem.link}
                    onClick={onItemClick}
                    className="flex items-center justify-between rounded-2xl px-3 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100 hover:text-neutral-950"
                  >
                    <span>{subItem.name}</span>
                    <span className="text-xs text-neutral-400">{">"}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </nav>
  );
};

export const MobileNav =({ children, className, visible }: MobileNavProps) => {
  return (
    <div
      style={{
        backdropFilter: visible ? "blur(10px)" : "none",
        boxShadow: visible
          ? "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset"
          : "none",
      }}
      className={cn(
        "relative z-50 mx-auto flex max-w-[calc(100vw-2rem)] flex-col items-center justify-between bg-transparent py-2 transition-all duration-300 xl:hidden",
        visible
          ? "w-[90%] translate-y-5 rounded-2xl bg-white/80 px-3 dark:bg-neutral-950/80"
          : "w-full translate-y-0 rounded-[2rem] px-0",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const MobileNavHeader = ({
  children,
  className,
}: MobileNavHeaderProps) => {
  return (
    <div
      className={cn(
        "flex w-full flex-row items-center justify-between",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const MobileNavMenu = ({
  children,
  className,
  isOpen,
  onClose: _onClose,
}: MobileNavMenuProps) => {
  const { locale } = useI18n();

  return (
    isOpen ? (
      <nav
        id={MOBILE_NAV_MENU_ID}
        aria-label={localizedText(locale, "Huvudmeny", "Main menu")}
        className={cn(
          "absolute inset-x-0 top-16 z-50 flex w-full flex-col items-start justify-start gap-4 rounded-lg bg-white px-4 py-8 shadow-[0_0_24px_rgba(34,_42,_53,_0.06),_0_1px_1px_rgba(0,_0,_0,_0.05),_0_0_0_1px_rgba(34,_42,_53,_0.04),_0_0_4px_rgba(34,_42,_53,_0.08),_0_16px_68px_rgba(47,_48,_55,_0.05),_0_1px_0_rgba(255,_255,_255,_0.1)_inset] animate-in fade-in-0 duration-150 dark:bg-neutral-950",
          className,
        )}
      >
        {children}
      </nav>
    ) : null
  );
};

export const MobileNavToggle = ({
  isOpen,
  onClick,
}: {
  isOpen: boolean;
  onClick: () => void;
}) => {
  const { t } = useI18n();

  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={isOpen}
      aria-controls={isOpen ? MOBILE_NAV_MENU_ID : undefined}
      aria-label={isOpen ? t("siteHeader.closeMenu") : t("siteHeader.openMenu")}
      className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-neutral-200 bg-white text-black transition-colors hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]"
    >
      {isOpen ? <IconX className="h-5 w-5" /> : <IconMenu2 className="h-5 w-5" />}
    </button>
  );
};

